// The banner on a variant whose classification a source changed (TGCLOUD-1899): what the source
// said before and says now, when, and under which release, with Acknowledge for the group. The
// record's own values stay as they are above it: the banner reports the source's change and
// decides nothing. Drawn only while an event is open; acknowledging takes it away for everyone.
import React from 'react';
import { Box, Button, Chip, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { SnackContext } from 'src/store/ContextStore';
import { useAutoTranslation, useLanguage } from 'src/hooks/useTranslate';
import { formatLocalDate } from 'src/i18n/dates';
import {
  describeVariantMonitoringError,
  useAcknowledgeEvents
} from './variantMonitoringApi';
import { DIRECTION_LABELS, SOURCE_LABELS } from './variantMonitoringDisplay';

const DATE = { year: 'numeric', month: 'short', day: 'numeric' };

export default function ReclassificationBanner({ events = [], disabled }) {
  const t = useAutoTranslation();
  const lang = useLanguage();
  const ack = useAcknowledgeEvents();
  const { setSnack } = React.useContext(SnackContext) || {};
  const open = (Array.isArray(events) ? events : []).filter(
    (e) => e && !e.acknowledgedAt
  );
  if (!open.length) return null;

  const acknowledge = () =>
    ack.mutate(
      open.map((e) => String(e._id)),
      {
        onError: (err) =>
          setSnack?.({
            status: true,
            msg: describeVariantMonitoringError(
              err,
              t('The change could not be acknowledged.')
            ),
            severity: 'error'
          })
      }
    );

  return (
    <Box
      role="status"
      sx={{
        mt: 1,
        p: 1,
        borderRadius: '6px',
        border: '1px solid',
        borderColor: 'warning.light',
        backgroundColor: (theme) => alpha(theme.palette.warning.light, 0.12)
      }}
    >
      {open.map((e) => {
        const source = t(SOURCE_LABELS[e.source] || e.source);
        return (
          <Box
            key={e._id}
            sx={{
              display: 'flex',
              gap: 0.75,
              flexWrap: 'wrap',
              alignItems: 'center',
              mb: 0.5
            }}
          >
            <Typography sx={{ fontSize: 12.5, fontWeight: 600 }}>
              {e.direction === 'withdrawn'
                ? t(
                    '{{source}} no longer confirmed this classification on {{date}}',
                    {
                      source,
                      date: formatLocalDate(e.detectedAt, lang, DATE)
                    }
                  )
                : t('{{source}} reclassified this variant on {{date}}', {
                    source,
                    date: formatLocalDate(e.detectedAt, lang, DATE)
                  })}
            </Typography>
            <Chip
              size="small"
              variant="outlined"
              label={
                e.previousClassification
                  ? t('Was: {{value}}', { value: e.previousClassification })
                  : t('Was: no classification')
              }
            />
            <Chip
              size="small"
              variant="outlined"
              label={
                e.newClassification
                  ? t('Now: {{value}}', { value: e.newClassification })
                  : t('Now: no longer confirmed')
              }
            />
            <Chip
              size="small"
              label={t(DIRECTION_LABELS[e.direction] || e.direction)}
            />
            {e.sourceDatasetVersion && (
              <Typography sx={{ fontSize: 11.5, color: 'text.secondary' }}>
                {t('{{source}} release {{release}}', {
                  source,
                  release: e.sourceDatasetVersion
                })}
              </Typography>
            )}
          </Box>
        );
      })}
      {!disabled && (
        <Button
          size="small"
          variant="outlined"
          disabled={ack.isPending}
          onClick={acknowledge}
        >
          {open.length === 1
            ? t('Acknowledge for the group')
            : t('Acknowledge all for the group')}
        </Button>
      )}
    </Box>
  );
}
